import { useCallback, useEffect, useMemo, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"

import { queries } from "@productlane/zero/queries"

import type { ReplyType } from "./types"
import type { Editor } from "@tiptap/react"
import { quotedReplyContent } from "@/screens/app/threads/helpers/email-reply-history"
import {
	buildEmailHistoryHtml as buildEmailHistoryBlock,
	buildSignatureHtml,
} from "@/screens/app/threads/helpers/email-reply-scaffold"
import { useThreadContext } from "../../../thread-context"

interface UseEmailContentPopulationParams {
	composeEditor: Editor | null
	replyType: ReplyType
	isForwardMode: boolean
}

export function useEmailContentPopulation({
	composeEditor,
	replyType,
	isForwardMode,
}: UseEmailContentPopulationParams) {
	const { thread } = useThreadContext()
	const [user] = useQuery(queries.currentUser())
	const [hasClickedReply, setHasClickedReply] = useState(false)

	const emails = useMemo(
		() =>
			(thread.emails ?? []).filter(
				(e) => e.source !== "AUTO_REPLY" && e.threadId === thread.id,
			),
		[thread.emails, thread.id],
	)

	const latestEmail = useMemo(() => {
		if (emails.length === 0) return undefined
		return [...emails].sort((a, b) => b.createdAt - a.createdAt)[0]
	}, [emails])

	const currentLiveChat = thread.liveChatSession

	// Reset on thread switch so the new thread starts fresh and the auto-quote
	// guard re-runs. Without this, hasClickedReply can carry over from the
	// previous thread and skip auto-population on the new one.
	useEffect(() => {
		setHasClickedReply(false)
	}, [thread.id])

	// Builds the quoted thread-history block (`<div data-email-history>`) or "".
	// Pure: no editor mutation, so both the auto-quote trigger and the AI-draft
	// seed can reuse it to append the same history a manual reply would show.
	const buildEmailHistoryHtml = useCallback((): string => {
		if (replyType !== "email") return ""
		// Auto-quote guard: never embed an email whose threadId differs from
		// the currently active thread. Prevents cross-thread content leaks
		// when the composer is reused across thread switches and Zero
		// relations haven't fully hydrated yet.
		if (latestEmail && latestEmail.threadId !== thread.id) return ""
		const quotedContent = quotedReplyContent({
			thread: {
				origin: thread.origin,
				liveChatSession: currentLiveChat,
				contact: thread.contact,
				createdAt: thread.createdAt,
				text: thread.text,
				title: thread.title,
			},
			latestEmail,
			emailCount: emails.length,
			isForwardMode,
		})

		const signature =
			thread.origin !== "SLACK_CONNECT" && user?.memberships
				? buildSignatureHtml(user.memberships.emailSignature)
				: ""

		return buildEmailHistoryBlock({ signature, quotedContent })
	}, [
		replyType,
		latestEmail,
		thread.id,
		thread.text,
		thread.title,
		thread.origin,
		currentLiveChat,
		emails.length,
		thread.contact,
		thread.createdAt,
		user?.memberships,
		isForwardMode,
	])

	const handleEmailContentPopulation = useCallback(() => {
		if (replyType !== "email" || !composeEditor || !composeEditor.isEmpty)
			return
		const emailHistoryHTML = buildEmailHistoryHtml()
		if (emailHistoryHTML) {
			composeEditor.commands.setContent(`<p></p>${emailHistoryHTML}`)
			composeEditor.commands.focus("start", { scrollIntoView: false })
		}
	}, [replyType, composeEditor, buildEmailHistoryHtml])

	return {
		handleEmailContentPopulation,
		buildEmailHistoryHtml,
		hasClickedReply,
		setHasClickedReply,
		latestEmail,
		emails,
		replyAllByDefault: user?.replyAllByDefault ?? true,
	}
}
